/**
 * 客户对账单 PDF 版式常量（前后端共用）。
 * 位于仓库根目录 shared/，避免 Node 将文件归入 frontend 包导致模块类型歧义。
 */

/** 正文/表格：黑体系，数字与密排更清晰 */
export const STATEMENT_PDF_BODY_FONT_FAMILY = 'Noto Sans SC';

/** 主标题、章节条：衬线体更正式；仅嵌入 Bold（700）子集 */
export const STATEMENT_PDF_HEADING_FONT_FAMILY = 'Noto Serif SC';

export const STATEMENT_PDF_TEMPLATE = {
	pageWidth: '210mm',
	pagePadding: '9mm 11mm',
	pageFontSize: '12px',
	pageLineHeight: 1.35,
	/** 正文黑体 + 系统黑体系回退；标题单独用衬线（见 `.h1` / `.sectionTitle`） */
	pageFontFamily: `'${STATEMENT_PDF_BODY_FONT_FAMILY}', 'Noto Sans CJK SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', 'WenQuanYi Micro Hei', Arial, sans-serif`,
	headerGap: '8px',
	headerLeftFlex: 5,
	headerRightFlex: 7,
	boxBorder: '1px solid #111',
	boxPadding: '7px 8px',
	titleFontSize: '30px',
	companyNameFontSize: '13px',
	titleMargin: '25px 0 10px',
	titleLetterSpacing: '.5px',
	tableWrapMarginTop: '20px',
	tableHeaderBg: '#c8c8c8',
	tableCellPadding: '5px',
	tableCellSubFontSize: '11px',
	tableCellSubColor: '#666',
	emptyCellColor: '#666',
	emptyCellPadding: '16px',
	metaLineMarginBottom: '4px',
	/** 客户信息区：标题下缘、客户名称与下方 meta 行的间距（与 metaLine 节奏对齐） */
	customerSectionHeadingMarginBottom: '6px',
	customerLegalNameMarginBottom: '8px',
	totalLabelPadding: '6px 8px',
	totalCurrencyWidth: '72px',
	totalAmountWidth: '140px',
	remarkMarginTop: '20px',
	remarkPadding: '8px',
	paymentMarginTop: '20px',
	paymentTitlePadding: '6px 8px',
	paymentColPadding: '8px',
	logoMaxWidth: '260px',
	logoMaxHeight: '72px',
	logoFallbackColor: '#b0127d',
};

/** 离线字体挂在 CRA「public」下，构建后与预览同源路径（非 Puppeteer：服务端导出使用 base64 注入，见 buildStatementPdfDataUrlFontFaceCss）。 */
export const STATEMENT_PDF_FONT_FACE_PUBLIC_BASE = '/fonts/statement-pdf';

export const STATEMENT_PDF_FONT_FACE_CSS = `
@font-face {
	font-family: '${STATEMENT_PDF_BODY_FONT_FAMILY}';
	font-style: normal;
	font-weight: 400;
	font-display: swap;
	src: url(${STATEMENT_PDF_FONT_FACE_PUBLIC_BASE}/noto-sans-sc-chinese-simplified-400-normal.woff2) format('woff2');
}
@font-face {
	font-family: '${STATEMENT_PDF_BODY_FONT_FAMILY}';
	font-style: normal;
	font-weight: 700;
	font-display: swap;
	src: url(${STATEMENT_PDF_FONT_FACE_PUBLIC_BASE}/noto-sans-sc-chinese-simplified-700-normal.woff2) format('woff2');
}
@font-face {
	font-family: '${STATEMENT_PDF_HEADING_FONT_FAMILY}';
	font-style: normal;
	font-weight: 700;
	font-display: swap;
	src: url(${STATEMENT_PDF_FONT_FACE_PUBLIC_BASE}/noto-serif-sc-chinese-simplified-700-normal.woff2) format('woff2');
}
`.trim();

/** 对账单表列数：合同、发票、订单/款项、日期、超期、币种、应收余额 */
export const STATEMENT_PDF_TABLE_COLUMN_COUNT = 7;

/** 默认表头（与根目录 `.env` 中 `STATEMENT_PDF_CONFIG.template.text.tableHeaders` 对齐；预览与 PDF 共用 merge） */
export const STATEMENT_PDF_DEFAULT_TABLE_HEADERS = Object.freeze([
	'合同编号',
	'发票号码',
	'订单编号/款项阶段',
	'起止日期',
	'超期天数',
	'币种',
	'应收余额',
]);

export const STATEMENT_PDF_CSS = `
* { box-sizing: border-box; }
body {
	font-family: ${STATEMENT_PDF_TEMPLATE.pageFontFamily};
	color: #111;
	margin: 0;
	font-size: ${STATEMENT_PDF_TEMPLATE.pageFontSize};
	line-height: ${STATEMENT_PDF_TEMPLATE.pageLineHeight};
}
.page {
	position: relative;
	width: ${STATEMENT_PDF_TEMPLATE.pageWidth};
	max-width: 100%;
	box-sizing: border-box;
	padding: ${STATEMENT_PDF_TEMPLATE.pagePadding};
	margin: 0 auto;
}
.statementAsOfCorner {
	position: absolute;
	top: 2mm;
	right: 3mm;
	z-index: 3;
	display: flex;
	align-items: baseline;
	justify-content: flex-end;
	flex-wrap: nowrap;
	gap: 0.28em;
	max-width: 68%;
	box-sizing: border-box;
	padding: 0;
	line-height: 1.35;
	font-size: 9.5px;
	font-weight: 500;
	color: #737373;
	letter-spacing: 0.02em;
	-webkit-font-smoothing: antialiased;
}
.statementAsOfCornerLabel {
	font-weight: 500;
	color: #737373;
}
.statementAsOfCornerDate {
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	font-feature-settings: 'tnum' 1;
	color: #171717;
	letter-spacing: 0.03em;
}
.row {
	display: flex;
	gap: ${STATEMENT_PDF_TEMPLATE.headerGap};
	flex-wrap: nowrap;
	align-items: stretch;
	margin: 0;
	width: 100%;
	max-width: 100%;
	--bs-gutter-x: 0;
	--bs-gutter-y: 0;
}
.box {
	border: ${STATEMENT_PDF_TEMPLATE.boxBorder};
	padding: ${STATEMENT_PDF_TEMPLATE.boxPadding};
	box-sizing: border-box;
	width: 100%;
	max-width: 100%;
}
.h1 {
	font-family: '${STATEMENT_PDF_HEADING_FONT_FAMILY}', 'Noto Serif CJK SC', 'Songti SC', 'SimSun', serif;
	font-size: ${STATEMENT_PDF_TEMPLATE.titleFontSize};
	font-weight: 700;
	margin: ${STATEMENT_PDF_TEMPLATE.titleMargin};
	letter-spacing: ${STATEMENT_PDF_TEMPLATE.titleLetterSpacing};
	color: #111;
	line-height: 1.2;
}
.tableWrap {
	margin-top: ${STATEMENT_PDF_TEMPLATE.tableWrapMarginTop};
	width: 100%;
}
table {
	width: 100%;
	max-width: 100%;
	border-collapse: collapse;
	font-size: 12px;
	table-layout: auto;
	margin: 0;
}
th, td {
	border: ${STATEMENT_PDF_TEMPLATE.boxBorder};
	padding: ${STATEMENT_PDF_TEMPLATE.tableCellPadding};
	vertical-align: top;
	white-space: nowrap;
	overflow: hidden;
}
th { background: ${STATEMENT_PDF_TEMPLATE.tableHeaderBg}; font-weight: 700; text-align: center; }
td { word-break: normal; }
/* 合同编号、发票列与表头对齐（预览与 Puppeteer PDF 共用本 CSS） */
.tableWrap tbody tr td:first-child,
.tableWrap tbody tr td:nth-child(2) { text-align: center; }
/* 仅“订单编号/款项阶段”列允许换行，其他列保持不换行 */
.tableWrap tbody tr td:nth-child(3) {
	white-space: normal;
	word-break: break-word;
}
.small { font-size: 12px; }
.metaLine { margin-bottom: ${STATEMENT_PDF_TEMPLATE.metaLineMarginBottom}; }
.totalRow {
	display: flex;
	flex-wrap: nowrap;
	padding: 0;
	font-size: 12px;
	width: 100%;
	margin: 0;
}
.totalRow .label {
	flex: 1;
	min-width: 0;
	padding: ${STATEMENT_PDF_TEMPLATE.totalLabelPadding};
	font-weight: 700;
}
.totalRow .currency {
	width: ${STATEMENT_PDF_TEMPLATE.totalCurrencyWidth};
	flex-shrink: 0;
	padding: ${STATEMENT_PDF_TEMPLATE.totalLabelPadding};
	text-align: center;
}
.totalRow .amount {
	width: ${STATEMENT_PDF_TEMPLATE.totalAmountWidth};
	flex-shrink: 0;
	padding: ${STATEMENT_PDF_TEMPLATE.totalLabelPadding};
	text-align: right;
	font-weight: 700;
}
.sectionTitle {
	font-family: '${STATEMENT_PDF_HEADING_FONT_FAMILY}', 'Noto Serif CJK SC', 'Songti SC', 'SimSun', serif;
	padding: ${STATEMENT_PDF_TEMPLATE.paymentTitlePadding};
	border-bottom: ${STATEMENT_PDF_TEMPLATE.boxBorder};
	font-weight: 700;
}
.statementPdfPageFooter {
	margin-top: 14px;
	padding-top: 8px;
	border-top: 1px solid #bbb;
	font-size: 10px;
	text-align: center;
	color: #444;
}
.footerGrid {
	display: flex;
	flex-wrap: nowrap;
	width: 100%;
	margin: 0;
}
.footerCol {
	width: 50%;
	flex: 1 1 50%;
	min-width: 0;
	padding: ${STATEMENT_PDF_TEMPLATE.paymentColPadding};
	box-sizing: border-box;
}
.footerCol + .footerCol { border-left: ${STATEMENT_PDF_TEMPLATE.boxBorder}; }
@page { size: A4 portrait; margin: 8mm; }
`;

const PREVIEW_ROOT = '.statement-pdf-react-preview';

/**
 * 由 STATEMENT_PDF_CSS 推导预览样式（仅加作用域前缀），改版式只维护 STATEMENT_PDF_CSS + 下方映射顺序。
 */
export function buildStatementPdfPreviewScopedCss() {
	let c = STATEMENT_PDF_CSS.replace(/@page\s*\{[^}]*\}\s*/g, '').trim();
	c = c.replace(/^\*\s*\{/, `${PREVIEW_ROOT} * {`);
	c = c.replace(/^body\s*\{/m, `${PREVIEW_ROOT} {`);
	const scoped = [
		[/^\.footerCol\s*\+\s*\.footerCol/gm, `${PREVIEW_ROOT} .footerCol + .footerCol`],
		[/^\.totalRow\s+\.amount/gm, `${PREVIEW_ROOT} .totalRow .amount`],
		[/^\.totalRow\s+\.currency/gm, `${PREVIEW_ROOT} .totalRow .currency`],
		[/^\.totalRow\s+\.label/gm, `${PREVIEW_ROOT} .totalRow .label`],
		[/^\.totalRow\b/gm, `${PREVIEW_ROOT} .totalRow`],
		[/^\.sectionTitle\b/gm, `${PREVIEW_ROOT} .sectionTitle`],
		[/^\.footerGrid\b/gm, `${PREVIEW_ROOT} .footerGrid`],
		[/^\.footerCol\b/gm, `${PREVIEW_ROOT} .footerCol`],
		[/^\.statementPdfPageFooter\b/gm, `${PREVIEW_ROOT} .statementPdfPageFooter`],
		[/^\.statementAsOfCornerDate\b/gm, `${PREVIEW_ROOT} .statementAsOfCornerDate`],
		[/^\.statementAsOfCornerLabel\b/gm, `${PREVIEW_ROOT} .statementAsOfCornerLabel`],
		[/^\.statementAsOfCorner\b/gm, `${PREVIEW_ROOT} .statementAsOfCorner`],
		[/^\.metaLine\b/gm, `${PREVIEW_ROOT} .metaLine`],
		[/^\.small\b/gm, `${PREVIEW_ROOT} .small`],
		[/^\.tableWrap\b/gm, `${PREVIEW_ROOT} .tableWrap`],
		[/^\.h1\b/gm, `${PREVIEW_ROOT} .h1`],
		[/^\.box\b/gm, `${PREVIEW_ROOT} .box`],
		[/^\.row\b/gm, `${PREVIEW_ROOT} .row`],
		[/^\.page\b/gm, `${PREVIEW_ROOT} .page`],
		[/^th,\s*td/gm, `${PREVIEW_ROOT} th, ${PREVIEW_ROOT} td`],
		[/^th\b/gm, `${PREVIEW_ROOT} th`],
		[/^td\b/gm, `${PREVIEW_ROOT} td`],
		[/^table\b/gm, `${PREVIEW_ROOT} table`],
	];
	for (const [re, to] of scoped) {
		c = c.replace(re, to);
	}
	return `${PREVIEW_ROOT} { min-width: 0; }\n${c}`;
}

/** 供前端直接注入（与 PDF 同源规则，仅作用域不同） */
export const STATEMENT_PDF_PREVIEW_SCOPED_CSS = buildStatementPdfPreviewScopedCss();
